import { graphql, useStaticQuery } from "gatsby";
import Markdown from "markdown-to-jsx";
import React from "react";
import { twMerge } from "tailwind-merge";
import { ContentBlockHeroDefaultType, SharpImage } from "../../../types/blocks";
import Image from "../../atoms/image";
import Link from "../../atoms/link";
import CTAs from "../../molecules/ctas";

type DataType = {
  heroImage: SharpImage;
};

const BlockHeroDefault = ({
  headline,
  copytext,
  ctas,
  showAnnouncement,
  announcement,
  showFeatured,
  featured,
  block_settings: { background_color, html_id } = {
    background_color: "bg-white dark:bg-secondary-900",
  },
}: Omit<ContentBlockHeroDefaultType, "type">) => {
  const data: DataType = useStaticQuery(query);

  return (
    <section className={twMerge(background_color, "scroll-mt-20")} id={html_id}>
      <Image
        image={data.heroImage}
        objectFit="cover"
        className="!absolute top-16 z-0 w-full h-[560px] sm:h-[640px] -translate-x-1/2 sm:top-8 md:top-44 max-w-[1440px] left-1/2 opacity-50"
        alt="network"
      />
      <div className="relative z-10 max-w-screen-lg px-4 py-8 mx-auto space-y-8 text-center lg:py-16 lg:px-12">
        {announcement && showAnnouncement && (
          <Link
            to={announcement.target}
            variant="none"
            size="none"
            className="inline-flex items-center justify-between px-1 py-1 pr-4 text-sm rounded-full text-secondary-700 bg-secondary-100 dark:bg-secondary-800 dark:text-white hover:bg-secondary-200 dark:hover:bg-secondary-700"
            role="alert"
          >
            <span className="text-xs bg-primary-600 rounded-full text-white px-4 py-1.5 mr-3">
              {announcement.chip}
            </span>{" "}
            <span className="text-sm font-medium">{announcement.text}</span>
            <svg
              className="w-5 h-5 ml-2"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              ></path>
            </svg>
          </Link>
        )}
        <div className="space-y-8">
          <h1 className="px-4 text-4xl font-extrabold leading-none tracking-tight text-secondary-900 md:text-5xl lg:text-6xl dark:text-white">
            <Markdown>{headline}</Markdown>
          </h1>
          <p className="font-normal text-secondary-500 md:text-lg sm:px-16 xl:px-48 dark:text-secondary-400">
            {copytext}
          </p>
        </div>
        {ctas.length > 0 && <CTAs ctas={ctas} className="justify-center" />}
        {featured && showFeatured && (
          <div className="px-4 py-8 mx-auto space-y-6 text-center md:max-w-screen-md lg:max-w-screen-lg lg:px-36">
            <span className="font-semibold uppercase text-secondary-400">
              {featured.topline}
            </span>
            <div className="flex flex-wrap items-center justify-center gap-6 text-secondary-500 sm:justify-between">
              {featured.logos.map((logo, index) => (
                <Link
                  variant="none"
                  size="none"
                  key={index}
                  to={logo.target}
                  className="transition-all duration-300 opacity-50 hover:opacity-100"
                >
                  <Image image={logo.image} alt="logo" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export const query = graphql`
  query HeroQuery {
    heroImage: file(relativePath: { eq: "images/hero/network-01-light.png" }) {
      childImageSharp {
        gatsbyImageData(width: 1440, placeholder: BLURRED, quality: 90)
      }
    }
  }
`;

export default BlockHeroDefault;
